import React, { useState, useMemo, useEffect, useCallback } from "react";
import Portal from "../Portal";
import ClickOutsideHolder from "../ClickOutsideHolder";
import "./styles.scss";

const OUTER_OFFSET = 8; // padding (maybe we could calculate padding dynamically as well?)

const SmartPortal = props => {
    const {
        children,
        portalClassName = "",
        portalKey = "SmartPortal",
        fieldKey,
        portalPlacement = "right",
        targetElement,
        repositionTrigger,
        delayedRepositionTrigger,
        delayedRepositionDelay = 500,
        outerOffset = OUTER_OFFSET,
        outerXOffset = 0,
        outerYOffset = 0,
        onClickOutside,
        sizeTrigger,
        onClickOutsidePreventFieldKeys = [],
        withTargetWidth,
        withWidthOffset,
        withoutRepositioning,
        onPositionUpdate,
        isSelectPortalMenuFlexible,
        excludePortalHeightFromCalculations
    } = props;

    const [portalWidth, setPortalWidth] = useState(0);
    const [portalHeight, setPortalHeight] = useState(0);

    const [isMounted, setIsMounted] = useState(false);

    const [repositionTimeout, setRepositionTimeout] = useState(null);

    // const [targetParent, setTargetParent] = useState(null);
    const [targetParentY, setTargetParentY] = useState(null);
    const [targetParentX, setTargetParentX] = useState(null);
    const [targetParentSticky, setTargetParentSticky] = useState(null);

    const [portalDimensions, setPortalDimensions] = useState(null);

    const handleScroll = useCallback(event => {
        const target = event.target;
        // prevent scroll trigger on target scroll
        if (target && target === targetElement) {
            return;
        }
        updatePortalDimensions();
    }, []);

    const handleClickOutside = e => {
        let isParent = false;
        let isComboBoxPortal = false;

        const path = e ? e.path || (e.composedPath && e.composedPath()) : [];
        for (let i = 0; i < path.length; i++) {
            if (path[i].getAttribute && path[i].getAttribute("data-portal-key")) {
                isParent = path[i].getAttribute("data-portal-key") === portalKey;
                // break;
            }
            if (path[i].getAttribute && path[i].getAttribute("data-portal-key")) {
                isComboBoxPortal = path[i].getAttribute("data-portal-key") === "SmartPortalComboboxMenu";
                // break;
            }
            if (onClickOutsidePreventFieldKeys && Array.isArray(onClickOutsidePreventFieldKeys)) {
                if (path[i].getAttribute && path[i].getAttribute("data-field-key")) {
                    isParent = onClickOutsidePreventFieldKeys.includes(path[i].getAttribute("data-field-key"));
                    // break;
                }
            }
        }
        if (!isParent && !isComboBoxPortal) {
            if (onClickOutside && typeof onClickOutside === "function") {
                onClickOutside(e);
            }
        }
    };

    const handlePortalSize = rect => {
        const { width, height } = rect;
        let finalWidth = width
        if (withTargetWidth && targetElement) {
            const { width: targetWidth } = targetElement.getBoundingClientRect();
            // target width is larger => fallback to it
            if (targetWidth > width) {
                finalWidth = targetWidth;
            }
            // width is larger than targetWidth => check if isSelectPortalMenuFlexible flag provided (for example it is used in DynamicFormPopup for grid_mode)
            if (width > targetWidth && !isSelectPortalMenuFlexible) {
                finalWidth = targetWidth;
            }
        }
        setPortalWidth(finalWidth);
        setPortalHeight(height);
    };

    const findTargetElementParent = () => {
        if (targetElement) {
            let yFound = false;
            let xFound = false;
            let offsetParent = targetElement.offsetParent;
            let offsetParentY = targetElement.offsetParent;
            let offsetParentX = targetElement.offsetParent;
            while (offsetParent) {
                const styles = getComputedStyle(offsetParent);
                const overflowY = styles.getPropertyValue("overflow-y");
                const overflowX = styles.getPropertyValue("overflow-x");
                if (!yFound && overflowY && (overflowY.includes("auto") || overflowY.includes("scroll"))) {
                    yFound = true;
                    offsetParentY = offsetParent;
                }
                if (!xFound && overflowX && overflowX.includes("scroll")) {
                    xFound = true;
                    offsetParentX = offsetParent;
                }

                if (yFound && xFound) {
                    break;
                }

                if (!offsetParent.offsetParent) {
                    if (!yFound) {
                        offsetParentY = offsetParent;
                    }
                    if (!xFound) {
                        offsetParentX = offsetParent;
                    }
                }
                offsetParent = offsetParent.offsetParent;
            }
            // we need to check if X parent is not inside of the portal (if it is - set body as a parent)
            let offsetParentXParent = offsetParentX;
            let isInsideXPortal = false;
            while (offsetParentXParent) {
                if (offsetParentXParent && offsetParentXParent.hasAttribute("data-portal-key")) {
                    isInsideXPortal = true;
                }
                offsetParentXParent = offsetParentXParent.offsetParent;
            }
            setTargetParentX(isInsideXPortal ? document.body : offsetParentX);
            // we need to check if Y parent is not inside of the portal (if it is - set body as a parent)
            let offsetParentYParent = offsetParentY;
            let isInsideYPortal = false;
            while (offsetParentYParent) {
                if (offsetParentYParent && offsetParentYParent.hasAttribute("data-portal-key")) {
                    isInsideYPortal = true;
                }
                offsetParentYParent = offsetParentYParent.offsetParent;
            }
            setTargetParentY(isInsideYPortal ? document.body : offsetParentY);
        }

    };

    const findTargetParentSticky = element => {
        const isBody = element && element === document.body;
        // only find sticky if parent element is not a body (otherwise it would find some sticky element which is not related to the target)
        if (element && !isBody) {
            let children = Array.from(element.children);
            while (children.length) {
                const child = children.shift();
                const styles = getComputedStyle(child);
                const position = styles.getPropertyValue("position");
                if (position && position === "sticky") {
                    setTargetParentSticky(child);
                    break;
                }
                if (child.children) {
                    children = [...children, ...child.children];
                }
            }
        }
    };

    const updatePortalDimensions = () => {
        if (repositionTimeout) {
            clearTimeout(repositionTimeout);
            setRepositionTimeout(null);
        }
        if (targetElement) {
            const targetRect = targetElement.getBoundingClientRect();
            const { top, bottom, right, left, width, height } = targetRect;
            setPortalDimensions({ top, bottom, right, left, width, height });
        } else {
            console.error("SmartPortal::updatePortalDimensions: target is not yet available");
        }
    };

    const getPortalOffsetsAndVisibility = (ele, containerY, containerX, stickyContainer) => {
        if (!ele) {
            console.error("SmartPortal::getPortalOffsetsAndVisibility: Target element _ele_ is empty!");
            return {
                yOffset: 0,
                xOffset: 0,
                isVisible: false,
                isReversed: false,
            };
        }
        const {
            bottom: elementBottom,
            top: elementTop,
            left: elementLeft,
            right: elementRight,
            height: elementHeight,
            width: elementWidth,
        } = ele.getBoundingClientRect();
        let isReversed = false;
        const containerRectY = containerY.getBoundingClientRect();
        const containerRectX = containerX.getBoundingClientRect();
        // adjust height based on sticker container
        // there was a case with the field controllers position inside of the customer panel
        // there was a sticky element on top, so we had to adjust the visibility based on this sticky container height

        let extraYOffset = 0;
        if (portalPlacement.includes("top")) {
            extraYOffset = portalHeight + outerYOffset;
        }
        if (portalPlacement.includes("bottom")) {
            extraYOffset = -1 * (elementHeight + outerOffset);
        }

        let extraXOffset = 0;
        if (portalPlacement.includes("inner-right")) {
            extraXOffset = portalWidth + outerOffset;
        }
        if (portalPlacement.includes("inner-left")) {
            extraXOffset = elementWidth + outerOffset;
        }

        if (portalPlacement === "left") {
            extraXOffset = elementWidth + portalWidth + outerXOffset;
            extraYOffset = extraYOffset + outerYOffset;
        }
        if (portalPlacement === "right") {
            extraXOffset = -1 * outerXOffset;
            extraYOffset = extraYOffset + outerYOffset;
        }

        let stickyContainerHeight = 0;
        if (stickyContainer) {
            const { height } = stickyContainer.getBoundingClientRect();
            stickyContainerHeight = height;
        }

        // console.log({stickyContainerHeight, extraYOffset});

        const portalDimensionsTop = excludePortalHeightFromCalculations
            ? portalDimensions.top + portalHeight - stickyContainerHeight
            : portalDimensions.top - stickyContainerHeight;

        // vertical (Y) checks
        const isBottomFit = portalDimensionsTop + stickyContainerHeight + portalHeight - extraYOffset < containerRectY.bottom;
        const isTopFit = portalDimensionsTop - extraYOffset > containerRectY.top;

        let newBottomOffset = portalDimensionsTop + portalHeight - extraYOffset - containerRectY.bottom;
        let newTopOffset = portalDimensionsTop - extraYOffset - containerRectY.top;

        const isElementBottomVisible = containerRectY.bottom > elementBottom - elementHeight;
        const isElementTopVisible = containerRectY.top + stickyContainerHeight - outerYOffset < elementTop + elementHeight + extraYOffset;

        // toggle portal switching to top or to the bottom once is not fits (if top or bottom positioned)
        if (portalPlacement.includes("bottom") && !isBottomFit) {
            newBottomOffset = portalHeight + elementHeight + outerOffset;
        }
        if (portalPlacement.includes("top") && !isTopFit) {
            newTopOffset = -1 * (portalHeight + elementHeight + outerOffset);
        }

        // make horizontal (X) checks
        let xOffset = -1 * outerOffset;
        const widthOffset = withWidthOffset ? portalWidth : 0;
        const rightOffset = elementRight + portalWidth + outerOffset + containerRectX.left - widthOffset - window.innerWidth;
        // const rightOffset = portalDimensions.right + portalWidth - containerRect.right;
        if (rightOffset > extraXOffset) {
            // swap right with left positioning
            if (portalPlacement === "right" && !withoutRepositioning) {
                const newXOffset = elementWidth + portalWidth + outerXOffset;
                if (newXOffset + portalWidth < window.innerWidth) {
                    xOffset = newXOffset;
                    extraXOffset = extraXOffset + outerXOffset;
                    isReversed = true;
                } else {
                    // same as below
                    xOffset = rightOffset - extraXOffset;
                }
            } else {
                xOffset = rightOffset - extraXOffset;
            }
        }
        if (portalPlacement === "left" && !withoutRepositioning) {
            if (portalWidth + outerXOffset > elementLeft) {
                const newXOffset = elementWidth + portalWidth + outerXOffset;
                if (newXOffset + portalWidth < window.innerWidth) {
                    xOffset = -1 * newXOffset;
                    extraXOffset = extraXOffset - outerXOffset;
                    isReversed = true;
                } else {
                    // move a little bit to the right
                    xOffset = -1 * (outerOffset + portalWidth);
                }
            }
        } else {
            const leftOffset = portalDimensions.right - portalWidth - containerRectX.left - outerOffset;
            if (leftOffset < 0) {
                // todo: remove that commented code once tested
                // xOffset = leftOffset;
                xOffset = -1 * outerOffset;
            }
        }

        // console.log({isBottomFit, isTopFit, newBottomOffset, newTopOffset, isElementBottomVisible, isElementTopVisible});
        

        if (isBottomFit && isTopFit) {
            return { yOffset: extraYOffset, xOffset: xOffset + extraXOffset, isVisible: true, isReversed };
        }
        // same as on top - but hide it
        if (withoutRepositioning) {
            return { yOffset: extraYOffset, xOffset: xOffset + extraXOffset, isVisible: false, isReversed };
        }
        if (!isBottomFit) {
            return {
                yOffset: newBottomOffset + extraYOffset,
                xOffset: xOffset + extraXOffset,
                isVisible: isElementBottomVisible,
                isReversed
            };
        }
        if (!isTopFit) {
            return {
                yOffset: newTopOffset + extraYOffset,
                xOffset: xOffset + extraXOffset,
                isVisible: isElementTopVisible,
                isReversed
            };
        }
    };

    const calculatedPosition = useMemo(() => {
        if (!portalDimensions) {
            return {};
        }
        const { isVisible, yOffset, xOffset, isReversed } = getPortalOffsetsAndVisibility(
            targetElement,
            targetParentY,
            targetParentX,
            targetParentSticky
        );
        const position = {
            top: portalHeight === 0 ? 0 : portalDimensions.top - yOffset,
            left: portalWidth === 0 ? 0 : portalDimensions.right - xOffset,
            isVisible,
            isReversed,
        };
        if (withWidthOffset) {
            position.left = position.left - portalWidth;
        }
        // edge case SDSUI-8489 / SDSUI-8490, target top dimension is not in viewport - stop updating dimensions in order to prevent weird jumpy UI
        if (!isReversed && portalDimensions.top > window.innerHeight) {
            position.top = 0;
            position.left = 0;
            position.isVisible = false;
        }
        return position;
    }, [portalPlacement, targetElement, portalDimensions, portalWidth, portalHeight]);

    const isPortalReady = useMemo(() => {
        if (withTargetWidth && !portalWidth) {
            return false;
        }
        if (!portalWidth && !portalHeight) {
            return false;
        }
        if (!calculatedPosition.isVisible || repositionTimeout) {
            return false;
        }
        return portalWidth && portalHeight;
    }, [portalWidth, portalHeight, calculatedPosition, repositionTimeout]);

    useEffect(() => {
        if (calculatedPosition && onPositionUpdate && typeof onPositionUpdate === 'function') {
            onPositionUpdate(calculatedPosition);
        }
    }, [calculatedPosition]);

    useEffect(() => {
        if (targetParentY) {
            findTargetParentSticky(targetParentY);
        }
    }, [targetParentY]);

    // if portal will be closed by click outside - maybe we don't need such useEffect
    useEffect(() => {
        // console.log({repositionTrigger});
        updatePortalDimensions();
    }, [repositionTrigger]);

    // if portal will be closed by click outside - maybe we don't need such useEffect
    useEffect(() => {
        if (!isMounted) {
            return;
        }
        if (repositionTimeout) {
            clearTimeout(repositionTimeout);
            setRepositionTimeout(null);
        }
        setRepositionTimeout(setTimeout(updatePortalDimensions, delayedRepositionDelay));
    }, [delayedRepositionTrigger]);

    useEffect(() => {
        setIsMounted(true);
        findTargetElementParent();

        setTimeout(updatePortalDimensions, 0);
        document.addEventListener("scroll", handleScroll, true);
        window.addEventListener("resize", handleScroll, true);

        if (withTargetWidth && targetElement) {
            const { width } = targetElement.getBoundingClientRect();
            setPortalWidth(width);
        }

        return () => {
            document.removeEventListener("scroll", handleScroll, true);
            window.removeEventListener("resize", handleScroll, true);
        };
    }, []);

    return (
        <Portal
            className={`smart-portal ${portalClassName}`}
            dataPortalKey={portalKey}
            dataFieldKey={fieldKey}
            position={calculatedPosition}
            onSizeReady={handlePortalSize}
            sizeTrigger={sizeTrigger}
            isVisible={isPortalReady}
            portalWidth={withTargetWidth && portalWidth}
        >
            <ClickOutsideHolder className="smart-portal-click-outside-holder" onClickOutside={handleClickOutside}>
                {children}
            </ClickOutsideHolder>
        </Portal>
    );
};

export default SmartPortal;
